import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  convertCustomerIntakeResponseToRegistration,
  convertCustomerIntakeResponseToTrialBooking,
  CustomerIntakeConversionPrepare,
  CustomerIntakeResponseDetail,
  getCustomerIntakeErrorMessage,
  prepareCustomerIntakeResponseConversion,
} from '../../utils/customerIntakeApi';

type CustomerIntakeConversionActionsProps = {
  response: CustomerIntakeResponseDetail;
  onConversionCompleted?: () => void;
};

const today = () => new Date().toISOString().slice(0, 10);

const statusMessage: Record<CustomerIntakeConversionPrepare['status'], string> =
  {
    READY: 'The response is ready for conversion.',
    NEEDS_PHONE: 'A valid phone number is required before conversion.',
    NEEDS_NAME: 'A full name is required before creating the user.',
    NEEDS_DATE_OF_BIRTH:
      'Date of birth is required when no existing user matches this phone number.',
    SERVICE_BINDING_REQUIRED:
      'This form has no service package binding. Edit the form and import a configured package first.',
    PACKAGE_REQUIRED: 'Select the package to use for this conversion.',
    PACKAGE_UNAVAILABLE:
      'The package binding is no longer available. Refresh the form configuration.',
  };

const CustomerIntakeConversionActions = ({
  response,
  onConversionCompleted,
}: CustomerIntakeConversionActionsProps) => {
  const [prepared, setPrepared] =
    useState<CustomerIntakeConversionPrepare | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [trialOpen, setTrialOpen] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [trialDate, setTrialDate] = useState(today());
  const [registrationDate, setRegistrationDate] = useState(today());
  const [trialNote, setTrialNote] = useState('');
  const [registrationNote, setRegistrationNote] = useState('');
  const [discountPrice, setDiscountPrice] = useState('0');
  const [amount, setAmount] = useState('1');
  const [subAmount, setSubAmount] = useState('0');
  const [paymentMethod, setPaymentMethod] = useState<
    'CASH' | 'BANK_TRANSFER' | 'DEPOSIT'
  >('CASH');

  const loadPreparation = useCallback(async (packageId?: number) => {
    setLoading(true);
    setError('');
    try {
      setPrepared(
        await prepareCustomerIntakeResponseConversion(
          String(response.versionId),
          response.id,
          packageId,
        ),
      );
    } catch (loadError) {
      setError(
        getCustomerIntakeErrorMessage(
          loadError,
          'Unable to prepare this response for conversion.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [response.id, response.versionId]);

  useEffect(() => {
    void loadPreparation();
  }, [loadPreparation]);

  const completedActions = useMemo(
    () => new Set(prepared?.completedConversions.map((item) => item.action)),
    [prepared],
  );
  const selectedPackage = prepared?.selectedPackage;
  const ready = prepared?.status === 'READY' && Boolean(selectedPackage);
  const canTrial = ready && Boolean(selectedPackage?.trialAvailable);
  const canRegistration = ready;

  const handleTrialSubmit = async () => {
    if (!selectedPackage) return;
    setLoading(true);
    setError('');
    try {
      await convertCustomerIntakeResponseToTrialBooking(
        String(response.versionId),
        response.id,
        { packageId: selectedPackage.packageId, trialDate, note: trialNote },
      );
      setSuccess('Converted to Trial Booking successfully.');
      setTrialOpen(false);
      await loadPreparation(selectedPackage.packageId);
      onConversionCompleted?.();
    } catch (submitError) {
      setError(
        getCustomerIntakeErrorMessage(
          submitError,
          'Unable to create the Trial Booking.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationSubmit = async () => {
    if (!selectedPackage) return;
    setLoading(true);
    setError('');
    try {
      await convertCustomerIntakeResponseToRegistration(
        String(response.versionId),
        response.id,
        {
          packageId: selectedPackage.packageId,
          sos: registrationDate,
          note: registrationNote,
          discountPrice: Number(discountPrice) || 0,
          amount: Math.max(1, Number(amount) || 1),
          subAmount: Math.max(0, Number(subAmount) || 0),
          paymentMethod,
        },
      );
      setSuccess('Converted to Registration successfully.');
      setRegistrationOpen(false);
      await loadPreparation(selectedPackage.packageId);
      onConversionCompleted?.();
    } catch (submitError) {
      setError(
        getCustomerIntakeErrorMessage(
          submitError,
          'Unable to create the Registration.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.default',
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Convert response
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The phone number is matched against Users. If duplicates exist, the
            latest created user is selected deterministically.
          </Typography>
        </Box>

        {loading && !prepared && <CircularProgress size={22} />}

        {prepared?.user && (
          <Alert severity="info">
            User:{' '}
            <strong>{prepared.user.fullName || response.customerName}</strong>{' '}
            (ID {prepared.user.id})
            {prepared.match?.matchedBy === 'PHONE_LATEST_CREATED' &&
              ` — selected latest of ${prepared.match.candidateCount} matching users.`}
            {prepared.match?.matchedBy === 'CREATED' &&
              ' — a new user was created from this response.'}
          </Alert>
        )}

        {prepared && prepared.packages.length > 1 && (
          <FormControl fullWidth size="small">
            <InputLabel id={`conversion-package-${response.id}`}>
              Service package
            </InputLabel>
            <Select
              labelId={`conversion-package-${response.id}`}
              value={selectedPackage?.packageId || ''}
              label="Service package"
              disabled={loading}
              onChange={(event) =>
                void loadPreparation(Number(event.target.value))
              }
            >
              {prepared.packages.map((item) => (
                <MenuItem key={item.packageId} value={item.packageId}>
                  {item.serviceTypeName} / {item.subtypeName} /{' '}
                  {item.packageName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {prepared && prepared.status !== 'READY' && (
          <Alert severity="warning">{statusMessage[prepared.status]}</Alert>
        )}
        {selectedPackage && (
          <Typography variant="body2" color="text.secondary">
            Selected package: <strong>{selectedPackage.packageName}</strong> ·{' '}
            {selectedPackage.serviceTypeName} / {selectedPackage.subtypeName}
          </Typography>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            disabled={
              !canTrial || loading || completedActions.has('TRIAL_BOOKING')
            }
            onClick={() => setTrialOpen(true)}
          >
            {completedActions.has('TRIAL_BOOKING')
              ? 'Trial Booking completed'
              : 'Convert to trial booking'}
          </Button>
          <Button
            variant="outlined"
            disabled={
              !canRegistration ||
              loading ||
              completedActions.has('REGISTRATION')
            }
            onClick={() => setRegistrationOpen(true)}
          >
            {completedActions.has('REGISTRATION')
              ? 'Registration completed'
              : 'Convert to registration'}
          </Button>
        </Stack>
      </Stack>

      <Dialog
        open={trialOpen}
        onClose={() => setTrialOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Convert to Trial Booking</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Package: {selectedPackage?.packageName}
            </Typography>
            <TextField
              label="Trial date"
              type="date"
              value={trialDate}
              onChange={(event) => setTrialDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Note"
              value={trialNote}
              onChange={(event) => setTrialNote(event.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrialOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleTrialSubmit()}
            disabled={loading || !trialDate}
          >
            Convert
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={registrationOpen}
        onClose={() => setRegistrationOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Convert to Registration</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Package: {selectedPackage?.packageName}
            </Typography>
            <TextField
              label="Service start date"
              type="date"
              value={registrationDate}
              onChange={(event) => setRegistrationDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Amount"
                type="number"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                inputProps={{ min: 1 }}
                fullWidth
              />
              <TextField
                label="Sub amount"
                type="number"
                value={subAmount}
                onChange={(event) => setSubAmount(event.target.value)}
                inputProps={{ min: 0 }}
                fullWidth
              />
              <TextField
                label="Discount"
                type="number"
                value={discountPrice}
                onChange={(event) => setDiscountPrice(event.target.value)}
                inputProps={{ min: 0 }}
                fullWidth
              />
            </Stack>
            <FormControl fullWidth>
              <InputLabel id={`conversion-payment-${response.id}`}>
                Payment method
              </InputLabel>
              <Select
                labelId={`conversion-payment-${response.id}`}
                value={paymentMethod}
                label="Payment method"
                onChange={(event) =>
                  setPaymentMethod(
                    event.target.value as 'CASH' | 'BANK_TRANSFER' | 'DEPOSIT',
                  )
                }
              >
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="BANK_TRANSFER">Bank transfer</MenuItem>
                <MenuItem value="DEPOSIT">Deposit</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Note"
              value={registrationNote}
              onChange={(event) => setRegistrationNote(event.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegistrationOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleRegistrationSubmit()}
            disabled={loading || !registrationDate}
          >
            Enroll registration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerIntakeConversionActions;
